import React from "react";
import { useTranslation } from "react-i18next";

// Status line under the logo list's header row (flag
// logoSearchFeedbackEnabled; see useLogoSearchFeedback). Pure presentation:
// everything it says comes from state the list already holds - 0 calls.
export interface LogoSearchStatusProps {
  // Term on screen: the draft while the user is still typing, otherwise the
  // query's search term.
  term: string;
  // Typing (debounce window) or a search-driven fetch in flight.
  isSearching: boolean;
  // List-search throttle (flag listSearchThrottleEnabled): the typed term is
  // under this minimum, so no search is coming for it - say so instead of
  // "Searching…". Undefined (throttle off, or term long enough) = as before.
  minCharsHint?: number;
  // GetAllLogos' totalCount; undefined = older shape -> "… on this page".
  totalCount?: number;
  // Rows on the current page (the fallback count).
  pageResultCount: number;
  onClear: () => void;
}

export function LogoSearchStatus({
  term,
  isSearching,
  minCharsHint,
  totalCount,
  pageResultCount,
  onClear,
}: LogoSearchStatusProps) {
  const { t } = useTranslation();
  const shownTerm = term.trim();
  let text: string | null = null;
  let showClear = false;
  if (minCharsHint !== undefined) {
    text = t("compGenericTable.searchMinChars", { n: minCharsHint });
  } else if (isSearching) {
    text = shownTerm
      ? t("logoList.searchSearching", { term: shownTerm })
      : t("logoList.searchLoadingAll");
  } else if (shownTerm) {
    const n = totalCount ?? pageResultCount;
    if (n === 0) {
      text = t("logoList.searchNoMatch", { term: shownTerm });
      showClear = true;
    } else if (totalCount === undefined) {
      text = t("logoList.searchMatchCountOnPage", { n, term: shownTerm });
    } else if (n === 1) {
      text = t("logoList.searchMatchOne", { term: shownTerm });
    } else {
      text = t("logoList.searchMatchCount", { n, term: shownTerm });
    }
  }
  // The live region stays mounted while the flag is on (a region inserted
  // together with its first text is not reliably announced); with nothing
  // to say it has no content, no padding and no height.
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="logo-search-status"
      className={
        text
          ? "px-8 -mt-2 pb-3 text-xs text-gray-600 flex items-center gap-x-3"
          : undefined
      }
    >
      {text && <span>{text}</span>}
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          className="px-3 py-1 rounded-md border border-borderGray bg-white font-semibold text-primary-700 hover:bg-gray-100 whitespace-nowrap"
        >
          {t("logoList.searchClear")}
        </button>
      )}
    </div>
  );
}
